// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// useReviewItem — data hook for a single hf_review_items row.
//
// Pattern mirrors useAgoraConsultationMessages (src/hooks/useAgoraConsultationMessages.ts):
//   - Fetch on mount via a useCallback refresh function
//   - Subscribe to Supabase Realtime postgres_changes for the specific row
//   - On change: re-fetch (single-row fetch is cheap; avoids stale-state logic)
//   - Clean up channel on unmount
//
// Uses @/integrations/supabase/client (the project's canonical client),
// consistent with useAgoraConsultationMessages and FeedbackWidget.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { HfReviewItem } from "./types";

export interface UseReviewItemResult {
  reviewItem: HfReviewItem | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Fetches and subscribes to a single hf_review_items row by ID.
 *
 * Starts with optimistic data from `initialData` if provided (e.g. from a
 * hands-free-transcribe response) so the UI renders immediately without a
 * round-trip. The Realtime subscription then keeps it in sync.
 *
 * @param reviewItemId  UUID of the hf_review_items row to watch.
 * @param initialData   Optional pre-fetched row data (avoids initial spinner).
 */
export function useReviewItem(
  reviewItemId: string | null | undefined,
  initialData?: HfReviewItem,
): UseReviewItemResult {
  const [reviewItem, setReviewItem] = useState<HfReviewItem | null>(
    initialData ?? null,
  );
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!reviewItemId) return;
    try {
      const { data, error: fetchErr } = await supabase
        .from("hf_review_items")
        .select(
          "id, workflow_type, status, proposed_data, resolution_summary, " +
          "committed_record_id, committed_table, reviewed_at, reviewed_by, " +
          "created_at, facility_id, transcript_id",
        )
        .eq("id", reviewItemId)
        .single();

      if (fetchErr) throw fetchErr;
      setReviewItem(data as unknown as HfReviewItem);
      setError(null);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to load review item";
      setError(msg);
    }
  }, [reviewItemId]);

  // Initial fetch (skipped if initialData was provided and matches the ID)
  useEffect(() => {
    if (!reviewItemId) return;
    if (initialData?.id === reviewItemId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await refresh();
      } catch {
        // error is already set in refresh()
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewItemId, initialData?.id, refresh]);

  // Realtime subscription — mirrors useAgoraConsultationMessages pattern
  useEffect(() => {
    if (!reviewItemId) return;

    const channel = supabase
      .channel(`hf_review_items:${reviewItemId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "hf_review_items",
          filter: `id=eq.${reviewItemId}`,
        },
        () => {
          refresh().catch(console.error);
        },
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [reviewItemId, refresh]);

  return { reviewItem, loading, error, refresh };
}
